import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { FileText, ExternalLink } from "lucide-react";
import { Badge } from "@/components/primitives/badge";
import { fetchInvoices } from "@/server/services/stripeService";
import { QUERY_KEYS } from "@/config/queryKeys";

function formatAmount(cents: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

interface InvoiceListProps {
  hideHeading?: boolean;
}

export default function InvoiceList({ hideHeading = false }: InvoiceListProps) {
  const { t } = useTranslation();

  const { data: invoices = [], isLoading } = useQuery({
    queryKey: QUERY_KEYS.stripeInvoices,
    queryFn: fetchInvoices,
    staleTime: Infinity, // invoices are immutable records — only invalidate on billing events
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return (
    <div className="flex flex-col gap-3">
      {!hideHeading && (
        <h3 className="text-base font-semibold">
          {t("subscription.billingHistory")}
        </h3>
      )}

      {isLoading && (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-10 animate-pulse rounded-md bg-muted" />
          ))}
        </div>
      )}

      {!isLoading && invoices.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {t("subscription.noInvoices")}
        </p>
      )}

      {!isLoading && invoices.length > 0 && (
        <div className="divide-y divide-border rounded-md border border-border">
          {invoices.map((invoice) => (
            <div
              key={invoice.id}
              className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm"
            >
              <span className="text-muted-foreground">
                {formatDate(invoice.created)}
              </span>

              <span className="font-medium">
                {formatAmount(invoice.amount_paid, invoice.currency)}
              </span>

              <Badge
                variant={invoice.status === "paid" ? "secondary" : "outline"}
                className="capitalize"
              >
                {invoice.status}
              </Badge>

              <div className="ml-auto flex items-center gap-2">
                {invoice.invoice_pdf && (
                  <a
                    href={invoice.invoice_pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                    aria-label={t("subscription.downloadPdf")}
                  >
                    <FileText className="size-3.5" />
                    {t("subscription.downloadPdf")}
                  </a>
                )}
                {invoice.hosted_invoice_url && (
                  <a
                    href={invoice.hosted_invoice_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                    aria-label={t("subscription.viewInvoice")}
                  >
                    <ExternalLink className="size-3.5" />
                    {t("subscription.viewInvoice")}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
